import type { SearchFilters } from '@shared/stores/searchStore';
import { CaseType, DateRange, SortBy } from '@shared/constants/searchConstants';
import type { CaseTypeValue, DateRangeValue, SortByValue } from '@shared/types/searchTypes';

/**
 * Serialize filter state to URL search params
 */
export function serializeFiltersToURL(filters: SearchFilters): URLSearchParams {
  const params = new URLSearchParams();

  // Query
  if (filters.query) {
    params.set('q', filters.query);
  }

  // Courts (comma-separated)
  if (filters.courts.length > 0) {
    params.set('courts', filters.courts.join(','));
  }

  // Domains (comma-separated)
  if (filters.domains.length > 0) {
    params.set('domains', filters.domains.join(','));
  }

  // Themas (comma-separated)
  if (filters.themas && filters.themas.length > 0) {
    params.set('themas', filters.themas.join(','));
  }

  // Wetsartikelen (comma-separated)
  if (filters.wetsartikelen.length > 0) {
    params.set('wetsartikelen', filters.wetsartikelen.join(','));
  }

  // Case type
  if (filters.caseType && filters.caseType !== CaseType.ALL) {
    params.set('type', filters.caseType);
  }

  // Date range
  if (filters.dateRange && filters.dateRange !== DateRange.ALL) {
    params.set('dateRange', filters.dateRange);

    // Custom date range
    if (filters.dateRange === DateRange.CUSTOM) {
      if (filters.customDateFrom) {
        params.set('from', formatDateForURL(filters.customDateFrom));
      }
      if (filters.customDateTo) {
        params.set('to', formatDateForURL(filters.customDateTo));
      }
    }
  }

  // Sort by
  if (filters.sortBy && filters.sortBy !== SortBy.DATE_DESC) {
    params.set('sort', filters.sortBy);
  }

  return params;
}

/**
 * Serialize pagination state to URL search params
 */
export function serializePaginationToURL(page: number): URLSearchParams {
  const params = new URLSearchParams();

  // Only add page param if not on page 1
  if (page && page > 1) {
    params.set('page', page.toString());
  }

  return params;
}

/**
 * Deserialize page number from URL search params
 */
export function deserializePageFromURL(searchParams: URLSearchParams): number | undefined {
  const pageStr = searchParams.get('page');
  if (pageStr) {
    const page = parseInt(pageStr, 10);
    // Validate page number (must be positive integer)
    if (!isNaN(page) && page > 0) {
      return page;
    }
  }
  return undefined;
}

/**
 * Deserialize URL search params to filter state
 */
export function deserializeURLToFilters(searchParams: URLSearchParams): Partial<SearchFilters> {
  const filters: Partial<SearchFilters> = {};

  // Query
  const query = searchParams.get('q');
  if (query) {
    filters.query = query;
  }

  // Courts
  const courts = searchParams.get('courts');
  if (courts) {
    filters.courts = courts.split(',').filter(Boolean);
  }

  // Domains
  const domains = searchParams.get('domains');
  if (domains) {
    filters.domains = domains.split(',').filter(Boolean);
  }

  // Themas
  const themas = searchParams.get('themas');
  if (themas) {
    filters.themas = themas.split(',').filter(Boolean);
  }

  // Wetsartikelen
  const wetsartikelen = searchParams.get('wetsartikelen');
  if (wetsartikelen) {
    filters.wetsartikelen = wetsartikelen.split(',').filter(Boolean);
  }

  // Case type
  const caseType = searchParams.get('type');
  if (caseType) {
    filters.caseType = caseType as CaseTypeValue;
  }

  // Date range
  const dateRange = searchParams.get('dateRange');
  if (dateRange) {
    filters.dateRange = dateRange as DateRangeValue;

    // Custom date range
    if (dateRange === 'custom') {
      const from = searchParams.get('from');
      const to = searchParams.get('to');

      if (from) {
        filters.customDateFrom = parseDateFromURL(from);
      }
      if (to) {
        filters.customDateTo = parseDateFromURL(to);
      }
    }
  }

  // Sort by
  const sortBy = searchParams.get('sort');
  if (sortBy) {
    filters.sortBy = sortBy as SortByValue;
  }

  return filters;
}

/**
 * Format date for URL (YYYY-MM-DD)
 * Handles both Date objects and ISO date strings (which can occur
 * after JSON deserialization from localStorage).
 */
function formatDateForURL(date: Date | string): string {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse date from URL (YYYY-MM-DD)
 */
function parseDateFromURL(dateStr: string): Date | undefined {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return undefined;

  const [, year, month, day] = match;
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

  // Check if date is valid
  if (isNaN(date.getTime())) return undefined;

  return date;
}

/**
 * Check if two filter states are equal
 */
export function areFiltersEqual(a: Partial<SearchFilters>, b: Partial<SearchFilters>): boolean {
  // Compare query
  if (a.query !== b.query) return false;

  // Compare arrays (courts, domains, themas, and wetsartikelen)
  if (!areArraysEqual(a.courts || [], b.courts || [])) return false;
  if (!areArraysEqual(a.domains || [], b.domains || [])) return false;
  if (!areArraysEqual(a.themas || [], b.themas || [])) return false;
  if (!areArraysEqual(a.wetsartikelen || [], b.wetsartikelen || [])) return false;

  // Compare other fields
  if (a.caseType !== b.caseType) return false;
  if (a.dateRange !== b.dateRange) return false;
  if (a.sortBy !== b.sortBy) return false;

  // Compare custom dates
  if (!areDatesEqual(a.customDateFrom, b.customDateFrom)) return false;
  if (!areDatesEqual(a.customDateTo, b.customDateTo)) return false;

  return true;
}

/**
 * Check if two arrays are equal (regardless of order)
 */
function areArraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, index) => val === sortedB[index]);
}

/**
 * Check if two dates are equal.
 * Handles both Date objects and ISO date strings (which can occur
 * after JSON deserialization from localStorage).
 */
function areDatesEqual(a?: Date | string, b?: Date | string): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  const dateA = a instanceof Date ? a : new Date(a);
  const dateB = b instanceof Date ? b : new Date(b);
  return dateA.getTime() === dateB.getTime();
}

/**
 * Get a shareable URL with current filters
 */
export function getShareableURL(filters: SearchFilters, baseURL: string = window.location.origin): string {
  const params = serializeFiltersToURL(filters);
  const queryString = params.toString();
  return queryString ? `${baseURL}/search?${queryString}` : `${baseURL}/search`;
}

/**
 * Clean empty values from URL params
 */
export function cleanURLParams(searchParams: URLSearchParams): URLSearchParams {
  const cleaned = new URLSearchParams();

  for (const [key, value] of searchParams.entries()) {
    if (value && value !== 'all' && value !== 'date-desc') {
      cleaned.set(key, value);
    }
  }

  return cleaned;
}